/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Row, FlexContainer, theme } from "@nordcloud/gnui";
import { useGlobalCostByProviderQuery } from "~/generated/graphql";
import { LoaderWrap, ErrorWrap, BrickLoader, BarChart } from "~/components";
import { useCurrency } from "~/hooks";
import { useBillingPeriod } from "~/services/customers";
import { isNotEmpty, Maybe } from "~/tools";

import { DataCol } from "./CostPerProviderWidgetCols";
import { TableRows } from "./CostPerProviderWidgetLabels";

type ProviderData = {
  label: string;
  color: string;
  cost: string;
  forecast: string;
};

const filterZeroCost = (element: ProviderData) =>
  parseFloat(element.cost) !== 0;

const getBarValues = (data: ProviderData[]) =>
  data.map(({ color, cost }) => ({ color, value: parseFloat(cost) }));

const getCost = (data: Maybe<string>) => data ?? "";

export function CostPerProviderWidget() {
  const { isCurrentBillingPeriod } = useBillingPeriod();
  const { currency } = useCurrency();

  const { data, loading, error } = useGlobalCostByProviderQuery();

  const showForecast = isCurrentBillingPeriod;

  const {
    awsCost,
    azureCost,
    gcpCost,
    ibmCloudCost,
    kubernetesCost,
    openShiftCost,
  } = data?.organization ?? {};

  const providersData: ProviderData[] = [
    {
      label: "AWS",
      color: theme.color.support.orange,
      cost: getCost(awsCost?.currentMonth),
      forecast: getCost(awsCost?.forecast),
    },
    {
      label: "Azure",
      color: theme.color.support.blue,
      cost: getCost(azureCost?.currentMonth),
      forecast: getCost(azureCost?.forecast),
    },
    {
      label: "Google Cloud",
      color: theme.color.support.red,
      cost: getCost(gcpCost?.currentMonth),
      forecast: getCost(gcpCost?.forecast),
    },
    {
      label: "IBMCLOUD",
      color: theme.color.support.blueInverse,
      cost: getCost(ibmCloudCost?.currentMonth),
      forecast: getCost(ibmCloudCost?.forecast),
    },
    {
      label: "KUBERNETES",
      color: theme.color.support.indigo,
      cost: getCost(kubernetesCost?.currentMonth),
      forecast: getCost(kubernetesCost?.forecast),
    },
    {
      label: "OPENSHIFT",
      color: theme.color.support.cyan,
      cost: getCost(openShiftCost?.currentMonth),
      forecast: getCost(openShiftCost?.forecast),
    },
  ].filter(filterZeroCost);

  const barsValues = getBarValues(providersData);
  const showTab = isNotEmpty(providersData);

  return (
    <LoaderWrap loading={loading} Component={BrickLoader}>
      <ErrorWrap error={error}>
        <BarChart values={barsValues} />
        <FlexContainer>
          <Row>
            <TableRows showForecast={showForecast} show={showTab} />
            {providersData.map((provider) => (
              <DataCol
                key={provider.label}
                provider={provider.label}
                color={provider.color}
                forecast={provider.forecast}
                cost={provider.cost}
                showForecast={showForecast}
                currency={currency}
              />
            ))}
          </Row>
        </FlexContainer>
      </ErrorWrap>
    </LoaderWrap>
  );
}
